import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';

const SEOHead = ({
  title = 'Peptide Calculator - Retatrutide, Semaglutide & Tirzepatide Dosage',
  description = 'Free peptide calculator for reconstitution and dosage. Calculate exact doses for Retatrutide, Semaglutide, Tirzepatide, and other GLP-1 peptides.',
  peptide = null,
  isCalculator = false,
  faqItems = null,
}) => {
  // Base structured data for the application
  const baseStructuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebApplication',
        name: 'PeptideDose Calculator',
        url: 'https://peptidedose.com',
        applicationCategory: 'HealthApplication',
        operatingSystem: 'All',
        description:
          'Professional peptide reconstitution and dosage calculator for research peptides and weight loss medications',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        featureList: [
          'Peptide reconstitution calculator',
          'Dosage calculator',
          'Syringe volume visualization',
          'Support for multiple peptides',
          'Mobile responsive design',
        ],
      },
      {
        '@type': 'MedicalWebPage',
        name: title,
        description: description,
        url: 'https://peptidedose.com',
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          name: 'PeptideDose',
          url: 'https://peptidedose.com',
        },
        about: {
          '@type': 'MedicalEntity',
          name: 'Peptide Therapy',
          relevantSpecialty: 'Endocrinology',
        },
      },
    ],
  };

  // Peptide-specific structured data
  const peptideStructuredData = peptide
    ? {
        '@context': 'https://schema.org',
        '@type': 'Drug',
        name: peptide.name,
        alternateName: peptide.brands,
        description: peptide.summary,
        administrationRoute: 'Subcutaneous injection',
        dosageForm: 'Injectable solution',
        mechanismOfAction: peptide.mechanism,
        prescriptionStatus: peptide.status?.includes('FDA approved')
          ? 'PrescriptionOnly'
          : 'ResearchOnly',
        clinicalPharmacology: peptide.efficacy,
        warning: peptide.safety,
      }
    : null;

  // Calculator-specific structured data
  const calculatorStructuredData = isCalculator
    ? {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How to Calculate Peptide Dosage',
        description: 'Step-by-step guide to calculate peptide reconstitution and dosage',
        step: [
          {
            '@type': 'HowToStep',
            name: 'Enter peptide amount',
            text: 'Input the amount of peptide in milligrams (mg)',
          },
          {
            '@type': 'HowToStep',
            name: 'Add bacteriostatic water volume',
            text: 'Enter the volume of bacteriostatic water in milliliters (mL)',
          },
          {
            '@type': 'HowToStep',
            name: 'Specify desired dose',
            text: 'Input your desired dose in milligrams (mg)',
          },
          {
            '@type': 'HowToStep',
            name: 'Read syringe volume',
            text: 'The calculator will show the exact syringe volume to draw',
          },
        ],
        tool: {
          '@type': 'HowToTool',
          name: 'Insulin syringe',
        },
        supply: [
          {
            '@type': 'HowToSupply',
            name: 'Peptide vial',
          },
          {
            '@type': 'HowToSupply',
            name: 'Bacteriostatic water',
          },
        ],
      }
    : null;

  // FAQ structured data - only if faqItems are provided
  const faqStructuredData =
    faqItems && faqItems.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }
      : null;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Additional meta tags for specific peptides */}
      {peptide && (
        <>
          <meta
            name="keywords"
            content={`${peptide.name} calculator, ${peptide.name} dosage, ${peptide.name} reconstitution, ${peptide.brands || ''} calculator`}
          />
          <meta
            property="og:title"
            content={`${peptide.name} Calculator - Dosage & Reconstitution Tool`}
          />
          <meta property="og:description" content={peptide.summary} />
        </>
      )}

      {/* Structured Data */}
      <script type="application/ld+json">{JSON.stringify(baseStructuredData)}</script>

      {peptideStructuredData && (
        <script type="application/ld+json">{JSON.stringify(peptideStructuredData)}</script>
      )}

      {calculatorStructuredData && (
        <script type="application/ld+json">{JSON.stringify(calculatorStructuredData)}</script>
      )}

      {faqStructuredData && (
        <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
      )}
    </Helmet>
  );
};

SEOHead.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  peptide: PropTypes.shape({
    name: PropTypes.string.isRequired,
    brands: PropTypes.string,
    summary: PropTypes.string,
    mechanism: PropTypes.string,
    dosing: PropTypes.string,
    efficacy: PropTypes.string,
    safety: PropTypes.string,
    status: PropTypes.string,
  }),
  isCalculator: PropTypes.bool,
  faqItems: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string.isRequired,
      answer: PropTypes.string.isRequired,
    })
  ),
};

SEOHead.defaultProps = {
  title: 'Peptide Calculator - Retatrutide, Semaglutide & Tirzepatide Dosage',
  description:
    'Free peptide calculator for reconstitution and dosage. Calculate exact doses for Retatrutide, Semaglutide, Tirzepatide, and other GLP-1 peptides.',
  peptide: null,
  isCalculator: false,
  faqItems: null,
};

export default SEOHead;
